import { IDropdownOption } from "@fluentui/react";
import { RouterParams } from "App";
import { IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { EGroupCollectIntegrationStatus } from "global/Transform/GroupCollectEmitEventTransformer";
import { EHideReason } from "global/Visibility/EHideReason";
import { IVisibilityScope } from "global/Visibility/IVisibilityScope.type";
import { ParentVisibilityGraph } from "global/Visibility/ParentVisibilityGraph";
import {
    bcbs_bccadmin as IXrmTypes_bcbs_bccadmin,
    bcbs_config_attribute as IXrmTypes_bcbs_config_attribute,
    bcbs_config_attribute_bcbs_config_secti as IXrmTypes_bcbs_config_attribute_bcbs_config_secti,
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_masterattribute as IXrmTypes_bcbs_config_masterattribute,
    bcbs_config_optionset as IXrmTypes_bcbs_config_optionset,
    bcbs_config_optionsetitem as IXrmTypes_bcbs_config_optionsetitem,
    bcbs_config_release as IXrmTypes_bcbs_config_release,
    bcbs_config_row as IXrmTypes_bcbs_config_row,
    bcbs_config_section as IXrmTypes_bcbs_config_section,
    bcbs_config_segment as IXrmTypes_bcbs_config_segment,
    bcbs_config_supercategory as IXrmTypes_bcbs_config_supercategory,
    bcbs_config_tab as IXrmTypes_bcbs_config_tab,
    bcbs_config_tierlevel as IXrmTypes_bcbs_config_tierlevel,
    bcbs_config_tierset as IXrmTypes_bcbs_config_tierset,
    bcbs_opportunitybenefit as IXrmTypes_bcbs_opportunitybenefit,
    bcbs_opportunityoption as IXrmTypes_bcbs_opportunityoption,
    bcbs_opportunityproduct as IXrmTypes_bcbs_opportunityproduct,
    bcbs_quoterequest as IXrmTypes_bcbs_quoterequest,
    opportunity as IXrmTypes_opportunity
} from "./CRM";
import * as ICustomXrmTypes from "./CRM.type";
import { IFlexibleFormAttributeSetDefinition, IFlexibleFormCategoryDefinition, IFlexibleFormNonStandardDefinition } from "./FlexibleFormModel";
import { ICheckList } from "./ICheckList";

export type TGuid = string;
export type TOptionSet = number;
export type TUnderWritingStatus = "pending" | "rejected" | "approved"

export interface ICESNumber {
    id: string;
    // this is the name
    cesNumber: string;
    deactivated: boolean;
};

export interface IValue {
    type: "entity" |
    "optionset" |
    "string" |
    "number" |
    "float" |
    "date" |
    "bool" |
    "multioptionset";
}

export interface IStringValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "string"
    value: string;
}

export interface IDateValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "date"
    value: string;
}
export interface IBooleanValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "bool"
    value: boolean;
}

export interface ITask {
    taskId: string;
    subject: string;
}

export interface INullableBooleanValue extends IValue {
    type: "bool"
    value: boolean | null
}
export interface INumberValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "number";
    value: number;
}

// Abstract
export interface IReferenceValue extends IValue, IObjectWithNameId {
    type: "entity";
    id: TGuid,
    name: string
}

// export interface IFeesAndPremiumsAttributeSetReference extends IReferenceValue {
//     type: "entity"
//     entityType: 'bcbs_admin' | 'account' | 'bcbs_config_optionsetitem' | 'bcbs_copaycoinoption' | string;
// }


export interface IEntityReferenceValue extends IReferenceValue {
    type: "entity"
    entityType: 'bcbs_admin' | 'account' | 'bcbs_config_optionsetitem' | 'bcbs_copaycoinoption' | string;
}

export interface IMultiselectOptionSetValueValue {
    id: string;
    name: string;
    value: number;
}

export interface IMultiselectOptionSetValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "multioptionset"
    entityType: 'bcbs_config_optionsetitem',
    value: string;
    options: IMultiselectOptionSetValueValue[];
}


export interface IOptionSetValue<T = number> extends IValue {
    type: "optionset",
    name: string;
    value: T;
}

export type TValue = IStringValue |
    INumberValue |
    IEntityReferenceValue |
    IBooleanValue |
    IOptionSetValue |
    IDateValue |
    IMultiselectOptionSetValue |
    null;

export type TFieldType = "primary" | "pcp" | "specialist";
export type TTierLevels = "Tier 1" | "Tier 2" | "Tier 3" | "In Network" | "Out of Network" | "Comprehensive";

// abstract
export interface IFlexibleFieldValue {
    id: TGuid;
    tierRef: IRef | null;

    //TODO change this enum? or drop it
    type: TFieldType;
    value: TValue;

    emitRefId?: string;
}

export interface IObject {
    id: TGuid;
    name: string;
    deactivated: boolean;
}

// TODO rename this to IFlexibleAttribute
export interface IFlexibleField {
    id: TGuid;

    //TODO subject to removal soon
    name: string;
    //TODO subject to removal soon
    masterAttributeID: TGuid;

    configRef: IRef;
    values: IFlexibleFieldValue[];
    visibility?: EHideReason;
}

// TODO rename this to INonStandardFieldSet
export interface INonStandard {
    id: TGuid;
    name: string;
    sectionRef: IRef | null; // this is to distinguish if null, then its tab level otherwise if populated we need to display at section level
    fields: IFlexibleField[];
    deactivated: boolean;
    referenceOnly?: boolean;
    partOfTemplate?: boolean;
}

export interface INonStandardTemplate extends INonStandard {
    linkedCategoryTypeId?: string;
    linkedCategoryName?: string;
    relationships?: ITemplateRelationship[];
}

export interface ITemplateRelationship {
    categoryID: string; //this can be group config, or benefit at the moment
    categoryType: string;
    sectionRefs?: ITemplateSectionRelationship[];
}

export interface ITemplateSectionRelationship {
    sectionId: TGuid;
    nonStandards?: IRef[];
}

export interface ITemplateEntityRelation {
    entityType: string; //can be benefit, fee and premium etc
    id: TGuid;
}

export interface IRef extends IObjectWithNameId {
}

export interface IBenefitRef extends IRef {
};

export interface IPlan {
    id: TGuid;
    name: string;
    feesAndPremiumsRef: IRef | null;
    grandFathered: IOptionSetValue | null;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    nonStandards: INonStandard[];
    benefitRefs: IBenefitRef[];
    fundingType: IOptionSetValue | null;
    billingOnlyPlan: boolean;
    deactivated: boolean;
}

export interface ITierLevel extends IXrmTypes_bcbs_config_tierlevel {
    id: TGuid;
    name: string;
    description: string;
    pcp: boolean;
    ion: boolean;
    isLabelDisabled: boolean;
}

// TODO rename this to category status
// TODO this extends is very important for later, we can carry on category information into snapshot, very helpful
export enum EBenefitStatus {
    "In-Progress" = 1 as number,
    "Released for Notification" = 2 as number, // this is flipped with a button from new->ready once they are ready
    "Cancelled" = 3 as number,
    "Underwriting Complete" = 4 as number, //  is set at the same time the whole GC is approved.
}

export type TBenefitStatusOptionSetValue = IOptionSetValue<EBenefitStatus>;

export enum EGroupConfigurationStatus {
    // ** NOTE these codes do not match the option set codes on front end
    // they were never intended to match 1 for 1 for status of the record
    "Active" = 1 as number,
    "Cancelled" = 3 as number,
    "Transferred to Other LOB" = 5 as number,

    // seems we can start matching ...
    // matching these should not be a problem as the behavior should be 
    // exactly same and its transient until actual cancellation gets recorded.
    "Cancellation in Progress" = 808900013 as number,
    "Transferred to Other LOB in Progress" = 808900014 as number,
}
export type TGroupConfigurationStatusOptionSetValue = IOptionSetValue<EGroupConfigurationStatus>;


export enum EProduct {
    "No" = 0 as number,
    "Yes" = 1 as number,
}

export type TProductOptionSetValue = IOptionSetValue<EProduct>;

//TODO I think IBenefit should extend from this
// id can be used on update broadcast
// we can store all ICategoryFields in single array lookup?
export interface ICategoryFields {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    tiers: ITierLevel[];
}

export type TDeactivateOptions = IBooleanValue;

export interface IBenefit extends IXrmTypes_bcbs_config_category, ICategoryBase {
    tiers: ITierLevel[];
    tierSetRef: IRef | null;
    grandFathered: IOptionSetValue | null;
    status: TBenefitStatusOptionSetValue;
    product: TProductOptionSetValue | null;
    fundingType: IOptionSetValue | null;

    // Association Template linking fields
    bcbs_associationtemplatebenefitid?: TGuid | null;
    bcbs_associationtemplatesnapshotid?: TGuid | null;
    /** Tracks how the benefit was linked: 'Import' = added new from template, 'Sync' = fields refreshed from template */
    bcbs_associationtemplateaction?: 'Import' | 'Sync' | null;
    /** True when this benefit was added or replaced by an Association Template */
    bcbs_fromassociationtemplate?: boolean;
}

export type TCategoryEntity = Partial<IPlan>
    & Partial<IBenefit>
    & Partial<IFeesAndPremiums>
    & Partial<IAccountsReceivable>
    & Partial<IStopLoss>
    & Partial<INonStandard>
    & Partial<IFlexibleFieldSet>;

export interface IGroupNumber {
    id: TGuid;
    cesNumberRef: IRef | null;
    prefix: string | null;
    base: string | null;
    suffix: string | null;

    // ***primary key
    // auto-calc = {prefix}-{base}-{suffix}
    groupNumber: string | null;
    fields: IFlexibleField[];
    accountsReceivableRef?: IRef | null;
    planRef?: IRef | null;
    stopLossRef?: IRef | null;
    deactivated: boolean;
    status?: TBenefitStatusOptionSetValue;
}

export interface IDepartment {
    id: TGuid;
    name: string | null;
    description: string | null;
    cesNumberRef: IRef | null;
    deactivated: boolean;
    fields: [];
}

export interface IDependency {
    originRef?: IRef;
}

export interface IAccountsReceivable extends ICategoryBase {

}

export interface IConfigAttribute {
    id: TGuid;
    name: string;
    sectionID: TGuid;
    masterAttributeRef: TGuid;
}

export interface INonStandardConfigAttribute extends IConfigAttribute {
    nonStandard: boolean;
    nonStandardAppliesTo: TOptionSet;
    nonStandardCustom: {
        categories: TGuid[],
        sections: TGuid[]
    }
}

export interface IAttributeSetConfigAttribute extends IConfigAttribute {
    attributeSetID: TGuid;
}

export interface IFlexibleFieldSet {
    id: TGuid;
    name: string;
    attributeSetRef: IRef;
    fields: IFlexibleField[];
    deactivated: boolean;
    dependecy?: IDependency;
}

export interface ICategoryBase {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    nonStandards: INonStandard[];
    deactivated: boolean;
}

export interface IFeesAndPremiums extends ICategoryBase {
    stopLossRef: IRef | null;
}

export enum ECommentStatus {
    New = 1,
    Resolved = 2
}

export enum ICommentRouteType {
    root = "root",
    plan = "plan",
    benefit = "benefit",
    feesAndPremiums = "feesAndPremiums",
    accountsReceivable = "accountsReceivable",
    stopLoss = "stopLoss",
    groupNumber = "groupNumber",
    nonStandard = "nonStandard",
    flexibleFieldSet = "flexibleFieldSet",

    tab = "tab",
    section = "section",

    configRef = "configRef",
    //or
    flexibleFieldRef = "flexibleFieldRef",

    flexibleFieldValueRef = "flexibleFieldValueRef",

}

// const route = {
//     name: "plan1",
//     id: "1234",
//     type: "plan",
//     nextSegment: {
//         name: "nonstandard1",
//         id: "5678",
//         type: "nonStandard",
//         nextSegment: {
//             name: "field1",
//             id: "9012",
//             type: "fieldRef"
//         }
//     }
// }


interface IAudit {
    by: IRef;
    on: string;
}

export interface ICommentRouteRef extends IRef {
    type?: ICommentRouteType
    next?: ICommentRouteRef;
}


export interface IComment {
    id: TGuid;
    comment: string;
    location: RouterParams & ICommentRouteRef;
    status: ECommentStatus;
    created?: IAudit;
    modified?: IAudit;
    onTarget?: boolean;
    onOrigin?: boolean;
    deactivated?: boolean;
}

export interface INotification {
    id: TGuid;
    name: string;
    title?: string;
    description?: string;
    deactivated?: boolean;
    location?: RouterParams;
    regarding?: () => any;
}

export interface IPharmacyORE {
    id: TGuid;
    releaseStatus: "In Progress" | "Ready for Release";
    releaseDate?: IDateValue | null;
    pharmacyPlanDesign: string;
    newPharmacyPlanDesign: string;
    newClientNumber: string;
}

export interface IGroupStructure {
    groupNumbers: IGroupNumber[];
    departments: IDepartment[];
    cesNumbers: ICESNumber[];

}

interface IMap {
    [key: number]: number;
}


export const bcbs_statecode_byStatus: IMap = {

    1: 0, // In Progress -> Active
    2: 1, // Underwriting Review Complete -> InActive
    808900000: 0, // Data Entry Complete -> Active
    808900001: 1, // Retired -> Inactive
    808900002: 1, // Inactive -> Inactive

    // todo is this correct
    808900003: 0, // MNIL Data Entry Complete -> Active ? 
    808900004: 1, // MNIL Underwriting Complete -> Inactive
    808900005: 0, // Corrections Pending -> Active
    808900006: 1, // Gold Standard -> Inactive

    // todo is this correct
    808900009: 0, // Pending Underwriting -> Active
    808900010: 1, // Merged and Closed -> Inactive

    808900012: 1, // "Transferred to Other LOB(808900012)" -> Inactive
    808900011: 1, // Cancelled -> Inactive
};

export interface IObjectWithNameId {
    id: TGuid;
    name: string;
}

export interface IGroupConfigurationData extends IObjectWithNameId {
    // used for schema compatibility check generated by group library app 
    // 0 - 0 day migration
    // 1 - Group Collection Integration
    // 
    iteration: number;
    id: TGuid;
    name: string;
    bcbs_shortname: string;
    effectiveDate: IDateValue | null;
    operativeDate: IDateValue | null;
    changeType: IOptionSetValue | null;
    fundingType: IOptionSetValue | null;
    dualFunded: IBooleanValue | null;
    erisa: IOptionSetValue | null;
    grandFathered: IOptionSetValue | null;
    MNIL: IBooleanValue | null;
    lineOfBusiness: IOptionSetValue | null;
    fields: IFlexibleField[];
    nonStandards: INonStandard[];
    nonStandardTemplates: INonStandardTemplate[];
    attributeSets: IFlexibleFieldSet[];
    plans: IPlan[];
    benefits: IBenefit[];
    comments: IComment[];
    groupStructure: IGroupStructure;
    claimsARs: IAccountsReceivable[];
    feesAndPremiums: IFeesAndPremiums[];
    stopLoss: IStopLoss[];
    showInActives: IBooleanValue | null;
    reconciliation: IBooleanValue | null;
    pharmacyORE?: IPharmacyORE[];
    status: TGroupConfigurationStatusOptionSetValue;
    releaseRef?: IRef;
    releaseVersion?: string;
    opportunity?: IEntityReferenceValue | null;
    groupCollectStatus?: EGroupCollectIntegrationStatus;
    isRevision: IBooleanValue | null;
    revisionTasks: IEntityReferenceValue[];
    notifications?: INotification[];

    // latest app version that touched the file
    appVersion: string;


    // Not used
    deactivated?: boolean;

    // use to flag which transformers have ran
    // make sure to update the c#/backend/model class and deploy app services

    transformers?: {
        Sprint17?: true,
        Sprint18?: true,
        Sprint21?: true,
        Sprint22?: true,
        Sprint24?: true,
        Sprint30?: true,
        Sprint33?: true,
        Sprint33PatchTransformer?: true,
        Sprint34?: true,
        Sprint35?: true,
        Sprint40?: true,
        Sprint43?: true,
    },

    audit?: {
        modifiedBy?: IEntityReferenceValue;
    },

    emit?: boolean;

}

export interface IStopLoss extends ICategoryBase {

}



export interface IGroupConfigurationConfig {
    bcbs_bccadmin: IXrmTypes_bcbs_bccadmin[];
    rows: IXrmTypes_bcbs_config_row[];
    segments: IXrmTypes_bcbs_config_segment[]
    categories: IXrmTypes_bcbs_config_category[],
    tabs: IXrmTypes_bcbs_config_tab[],
    sections: IXrmTypes_bcbs_config_section[],
    attributes: IXrmTypes_bcbs_config_attribute[],
    attributeNonStandardSections: IXrmTypes_bcbs_config_attribute_bcbs_config_secti[],
    masterattributes: IXrmTypes_bcbs_config_masterattribute[],
    optionsets: IXrmTypes_bcbs_config_optionset[],
    optionsetitems: IXrmTypes_bcbs_config_optionsetitem[]
    bcbs_config_supercategories: IXrmTypes_bcbs_config_supercategory[],

    tiersets: IXrmTypes_bcbs_config_tierset[],
    tierlevels: IXrmTypes_bcbs_config_tierlevel[],
    copaycoinoptions: ICustomXrmTypes.bcbs_config_categoryNavCopay[],
    globalOptionSetDefinitions: ICustomXrmTypes.IOptionSetDefinition[],

    // note: nothing from this should be saved or loaded
    // only runtime
    appOptionSets: {
        bcbs_fundingtypes: IOptionSetValue[] | undefined;
        bcbs_groupconfigurationchangetypecode: IOptionSetValue[] | undefined;
        bcbs_erisacode: IOptionSetValue[] | undefined;
        bcbs_lob: IOptionSetValue[] | undefined;
        bcbs_grandfatheredcode: IOptionSetValue[] | undefined;
        bcbs_idcardorderingoptioncode: IOptionSetValue[] | undefined;
    },

    // note: nothing from this should be saved or loaded
    // only used during runtime
    cache: {

        // bcbs_config_tab.bcbs_config_tabid > bcbs_config_tab
        map_bcbs_config_tab: Record<string, IXrmTypes_bcbs_config_tab>;

        map_bcbs_config_section: Record<string, IXrmTypes_bcbs_config_section>;
        map_bcbs_config_row: Record<string, IXrmTypes_bcbs_config_row>;

        map_bcbs_config_attribute: Record<string, IXrmTypes_bcbs_config_attribute>;
        map_bcbs_config_attribute_by_masterAttributeId: Record<string, IXrmTypes_bcbs_config_attribute[]>;
        map_bcbs_config_masterattribute: Record<string, IXrmTypes_bcbs_config_masterattribute>;
        map_bcbs_config_category: Record<string, IXrmTypes_bcbs_config_category>;
        map_bcbs_config_category_named: Record<ICustomXrmTypes.TCategoryName, IXrmTypes_bcbs_config_category>;
        map_bcbs_copaycoinoption_type: Record<string, ICustomXrmTypes.ICopayCoin[]>
        map_bcbs_config_attribute_sets: Record<string, IXrmTypes_bcbs_config_attribute[]>
        map_bcbs_bccadmin: Record<string, IXrmTypes_bcbs_bccadmin>;

        map_bcbs_config_masterattribute_cancellations: Record<string, IXrmTypes_bcbs_config_masterattribute>;
        // _bcbs_optionsetid_value > bcbs_config_optionsetitem[]
        map_bcbs_config_optionsetitems_bySetId: Record<string, IXrmTypes_bcbs_config_optionsetitem[]>;

        map_bcbs_config_optionsetitems: Record<string, IXrmTypes_bcbs_config_optionsetitem>;

        map_bcbs_config_tierlevels_by_tierSetId: Record<string, IXrmTypes_bcbs_config_tierlevel[]>;

        map_bcbs_config_categories_by_bcbs_config_supercategoryid: Record<string, Record<string, IXrmTypes_bcbs_config_category>>;
        map_bcbs_config_supercategory: Record<string, IXrmTypes_bcbs_config_supercategory>;
        map_bcbs_config_supercategory_by_bcbs_config_categoryid: Record<string, IXrmTypes_bcbs_config_supercategory>;

        bcbs_config_category_benefits: IXrmTypes_bcbs_config_category[];

        options_bcbs_lob: IDropdownOption<IOptionSetValue>[]
        options_bcbs_fundingtypes: IDropdownOption<IOptionSetValue>[]
        options_bcbs_erisacode: IDropdownOption<IOptionSetValue>[]
        options_bcbs_grandfatheredcode: IDropdownOption<IOptionSetValue>[]
        options_bcbs_changetypecode: IDropdownOption<IOptionSetValue>[]
        options_bcbs_idcardorderingoptioncode: IDropdownOption<IOptionSetValue>[]
        options_benefitStatusReasons: IDropdownOption<TBenefitStatusOptionSetValue>[],
        options_groupConfigurationStatusReasons: IDropdownOption<TGroupConfigurationStatusOptionSetValue>[],
        options_status: IDropdownOption<IBooleanValue>[];
        options_products: IDropdownOption<TProductOptionSetValue>[],

        // UI / Field generation helpers
        categoryFormDefinitions: Record<string, IFlexibleFormCategoryDefinition>;

        attributeSetFormDefinitions: Record<string, IFlexibleFormAttributeSetDefinition>;
        // TODO REPLACE attributeSetFormDefinitions ASAP
        newAttributeSetFormDefinitions: Record<string, IFlexibleFormAttributeSetDefinition>;

        nonStandardFormDefinitions: Record<string, IFlexibleFormNonStandardDefinition>;

        newNonStandardFormDefinitions: Record<string, IFlexibleFormNonStandardDefinition>;

        parentVisibilityGraph: ParentVisibilityGraph;

    }

    bcbs_config_release: IXrmTypes_bcbs_config_release;

    createdOnTime: number;
}

export type TScope = IGroupConfigurationData | IPlan | IBenefit | IFeesAndPremiums;

export type TCheckList = Record<string, ICheckList>;

export interface ITierDefinition {
    tierSet: IXrmTypes_bcbs_config_tierset | null;
    tierLevels: ITierLevel[];
};

export interface IOpportunityProductOptionBenefit extends IXrmTypes_bcbs_config_category {

    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    visibility: IVisibilityScope;

    bcbs_tierdefinition: ITierDefinition;

    deactivated: boolean;
    won?: boolean;
    requiresUpdate?: boolean;
    select?: boolean;

    bcbs_opportunitybenefits?: IXrmTypes_bcbs_opportunitybenefit[];
    bcbs_opportunityoption?: IXrmTypes_bcbs_opportunityoption;

    // ENROLLMENT
    bcbs_buyupuwsinglei?: number;
    bcbs_buyupuwfamilyi?: number;
    bcbs_buyupuweechildreni?: number;
    bcbs_buyupuweespousei?: number;
    bcbs_buyupuwtotali?: number;

    // RATES
    bcbs_buyupuwsinglem?: number;
    bcbs_buyupuwfamilym?: number;
    bcbs_buyupuweechildrenm?: number;
    bcbs_buyupuweespousem?: number;
    bcbs_buyupuwtotalm?: number;
    bcbs_annualpremium?: number;

    validationMessages: IValidationMessage[];
    bcbs_opportunityproduct?: IXrmTypes_bcbs_opportunityproduct;

    instructions: {
        onSaveOptionSkip?: boolean;
        onSaveOptionDeactivate?: boolean;
        onSaveOptionNoDelta?: boolean;
        onSaveOptionBenefitsSkip?: boolean;
    }

}

export interface IOpportunityProduct {
    opportunity: IXrmTypes_opportunity,
    options: IOpportunityProductOptionBenefit[];
    bcbs_opportunityproduct: IXrmTypes_bcbs_opportunityproduct;
    bcbs_quoterequest: IXrmTypes_bcbs_quoterequest,
}